import React, { useEffect, useLayoutEffect, useState } from 'react'
import { useTranslation } from 'react-i18next';
import { FaCar } from 'react-icons/fa';
import { HiOutlineRectangleGroup } from 'react-icons/hi2';
import { useDispatch } from 'react-redux';
import { vehicleGroupService } from '../../Services/vehicle.group.service';
import { vehicleService } from '../../Services/vehicle.service';
import { useSelected } from '../../contexts/SelectedContext';
import { Filters } from '../../components/Filters';
import SyncFusionDateTimePicker from '../Components/SyncFusionDateTimePicker/SyncFusionDateTimePicker';
import { TbFileTypePdf, TbFileTypeXls } from 'react-icons/tb';
import { reportService } from '../../Services/report.service';
import { downloadService } from '../../Services/download.service';
import { GridComponent, ColumnsDirective, ColumnDirective, Inject, Page } from '@syncfusion/ej2-react-grids';
import Spinner from '../Components/Spinner';
import { ToastComponent } from '../Components/ToastComponent';
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Tooltip from "react-bootstrap/Tooltip";
import EmptyData from '../../components/EmptyData';

const getDefaultDates = () => {
    const end = new Date();
    const start = new Date(end);
    start.setFullYear(end.getFullYear() - 1);
    return { start, end };
};

export const MaintenanceCompleted = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch(); /** eslint-disable-next-line */
    dispatch({ type: "title", value: t("MAINTENANCES.COMPLETED.TITLE") });

    const { selectedValues } = useSelected();
    const selectedGroup = selectedValues.veiculos?.[0] || 0;
    const selectedVehicle = selectedValues.veiculosGroups?.[0] || 0;

    const [vehicleGroups, setVehicleGroups] = useState([]);
    const [vehicles, setVehicles] = useState([]);
    const [, setFiltersLoading] = useState(false);
    const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [hasSearched, setHasSearched] = useState(false);
    const [toastParams, setToastParams] = useState(JSON.stringify({ show: false }));
    const [startDate, setStartDate] = useState(() => getDefaultDates().start);
    const [endDate, setEndDate] = useState(() => getDefaultDates().end);
    const [pageSize, setPageSize] = useState(10);

    const calculatePageSize = () => {
        const available = window.innerHeight - 320;
        setPageSize(Math.max(5, Math.floor(available / 30)));
    };

    useLayoutEffect(() => {
        calculatePageSize();
    }, []);

    useEffect(() => {
        let timeoutId;
        const handleResize = () => {
            clearTimeout(timeoutId);
            timeoutId = setTimeout(calculatePageSize, 300);
        };
        window.addEventListener("resize", handleResize);
        return () => {
            window.removeEventListener("resize", handleResize);
            clearTimeout(timeoutId);
        };
    }, []);

    const buildFilters = () => {
        const filters = { Completed: "S" };
        if (selectedGroup && selectedGroup !== "TODOS" && selectedGroup !== 0) filters.GroupId = selectedGroup;
        if (selectedVehicle && selectedVehicle !== "TODOS" && selectedVehicle !== 0) filters.VehicleId = selectedVehicle;
        if (startDate) filters.StartDate = new Date(startDate).toISOString().split("T")[0];
        if (endDate) filters.EndDate = new Date(endDate).toISOString().split("T")[0];
        return filters;
    };

    const fetchVehicleGroups = async () => {
        try {
            setFiltersLoading(true);
            const groups = await vehicleGroupService.fetchGroups();
            if (Array.isArray(groups)) {
                setVehicleGroups(groups);
                await fetchVehiclesByGroup(groups.length === 1 ? groups[0].vehicleGroupId : 0);
            } else {
                setVehicleGroups([]);
                await fetchVehiclesByGroup(0);
            }
        } catch (error) {
            console.log(error);
            setVehicleGroups([]);
        } finally {
            setFiltersLoading(false);
        }
    };

    const fetchVehiclesByGroup = async (groupId) => {
        setIsLoadingVehicles(true);
        try {
            const response = await vehicleService.fetchVehicles(groupId);
            setVehicles(Array.isArray(response) ? response : []);
        } catch (error) {
            console.log(error);
            setVehicles([]);
        } finally {
            setIsLoadingVehicles(false);
        }
    };

    const handleSearch = async () => {
        setHasSearched(true);
        setIsLoading(true);
        setIsSearching(true);
        try {
            const response = await reportService.fetchSummaryMaintenances(buildFilters());
            setTableData(Array.isArray(response) ? response : []);
        } catch (error) {
            console.log(error);
            setTableData([]);
        } finally {
            setIsLoading(false);
            setIsSearching(false);
        }
    };

    const handleDownloadXls = async () => {
        setIsLoading(true);
        try {
            const response = await reportService.fetchSummaryMaintenancesXls(buildFilters());
            if (response) downloadService.downloadApiFile(response, "xls", "manutencoes-concluidas");
            else setToastParams(JSON.stringify({ show: true, title: "Erro", body: "Não foi possível gerar o arquivo.", color: "danger" }));
        } catch (error) {
            setToastParams(JSON.stringify({ show: true, title: "Erro", body: error?.response?.data?.message || "Erro ao gerar XLS.", color: "danger" }));
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadPdf = async () => {
        setIsLoading(true);
        try {
            const response = await reportService.fetchSummaryMaintenancesPdf(buildFilters());
            if (response) downloadService.downloadApiFile(response, "pdf", "manutencoes-concluidas");
            else setToastParams(JSON.stringify({ show: true, title: "Erro", body: "Não foi possível gerar o arquivo.", color: "danger" }));
        } catch (error) {
            setToastParams(JSON.stringify({ show: true, title: "Erro", body: error?.response?.data?.message || "Erro ao gerar PDF.", color: "danger" }));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (selectedGroup !== null && selectedGroup !== undefined) {
            fetchVehiclesByGroup(selectedGroup);
        }
    }, [selectedGroup]);

    useEffect(() => {
        fetchVehicleGroups();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <div className="container-xxl flex-grow-1 container-p-y">
            <ToastComponent params={toastParams} closeToast={() => setToastParams(JSON.stringify({ show: false }))} />
            <p>{t("MAINTENANCES.COMPLETED.DESCRIPTION")}</p>
            <div className="filter-maintenance">
                <Filters
                    id={"veiculos"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.GROUPS")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_GROUPS")}
                    options={vehicleGroups || []}
                    icon={HiOutlineRectangleGroup}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                />
                <Filters
                    id={"veiculosGroups"}
                    isMulti={false}
                    title={t("VIEW_MAP_FILTERS.VEHICLES")}
                    hasSubTitle={true}
                    subTitle={t("VIEW_MAP_FILTERS.ALL_VEHICLES")}
                    options={vehicles || []}
                    icon={FaCar}
                    hasIcon={true}
                    pageTitle={"maintenances_scheduling"}
                    isLoading={isLoadingVehicles}
                />
                <div className="field">
                    <div className="field-data">
                        <SyncFusionDateTimePicker
                            id="datetimepickerStart"
                            value={startDate}
                            onChange={setStartDate}
                            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
                            step={30}
                        />
                    </div>
                    <span>{t("MAINTENANCES.COMPLETED.UNTIL")}</span>
                    <div className="field-data">
                        <SyncFusionDateTimePicker
                            id="datetimepickerEnd"
                            value={endDate}
                            onChange={setEndDate}
                            placeholder={t("SEARCH.INITIAL_DATE_TIME")}
                            step={30}
                        />
                    </div>
                </div>
                <button className="btn btn-primary" onClick={handleSearch} disabled={isLoading}>
                    {t("MAINTENANCES.COMPLETED.SEARCH")}
                </button>
            </div>

            <div className="buttons-pending">
                <button className="btn btn-primary" onClick={handleDownloadXls} disabled={isLoading}>
                    <TbFileTypeXls />
                    Excel
                </button>
                <button className="btn btn-secondary" onClick={handleDownloadPdf} disabled={isLoading}>
                    <TbFileTypePdf />
                    PDF
                </button>
            </div>

            {isLoading && <Spinner />}

            {!isSearching && tableData.length > 0 ? (
                <div className="mt-3">
                    <GridComponent dataSource={tableData} allowPaging rowHeight={30} pageSettings={{ pageSize }} height="auto">
                        <ColumnsDirective>
                            <ColumnDirective
                                field="plate"
                                headerText="Veículo"
                                width="120"
                                template={(props) => (
                                    <OverlayTrigger
                                        placement="top"
                                        overlay={
                                            <Tooltip id={`tooltip-vehicle-${props?.plate || props?.plateTip || "maintenance"}`}>
                                                {props?.plateTip || props?.maintenanceItem || props?.plate}
                                            </Tooltip>
                                        }
                                    >
                                        <span style={{ cursor: 'pointer' }}>{props?.plate}</span>
                                    </OverlayTrigger>
                                )}
                            />
                            <ColumnDirective
                                field="maintenanceItem"
                                headerText="Item"
                                width="150"
                            />
                            <ColumnDirective field="execution" headerText="Data Limite" width="90"
                                template={(props) => {
                                    const d = new Date(props.execution);
                                    if (!props.execution || d.getFullYear() <= 1) return null;
                                    return <span>{d.toLocaleDateString('pt-BR')}</span>;
                                }}
                            />
                            <ColumnDirective field="days" headerText="Dias Faltantes" width="120" textAlign="Right" />
                            <ColumnDirective field="datePercentage" headerText="%" width="70" textAlign="Right"                                template={(props) => <span>{props?.datePercentage ?? ''}</span>}
                            />
                            <ColumnDirective field="finalOdometer" headerText="Odometro Limite" width="130" textAlign="Right"
                                template={(props) => <span>{props?.finalOdometer ?? ''}</span>}
                            />
                            <ColumnDirective field="currentOdometer" headerText="KM Atual" width="100" textAlign="Right"
                                template={(props) => <span>{props?.currentOdometer ?? ''}</span>}
                            />
                            <ColumnDirective field="odometerPercentage" headerText="%" width="70" textAlign="Right"                                template={(props) => <span>{props?.odometerPercentage ?? ''}</span>}
                            />
                            <ColumnDirective
                                field="maintenanceStatus"
                                headerText="Status"
                                width="100"
                                textAlign="Center"
                                template={(props) => (
                                    <span style={props.maintenanceStatus === 'Atrasado' || props.maintenanceStatus === 3 ? { color: 'red', fontWeight: 'bold' } : {}}>
                                        {props.maintenanceStatus}
                                    </span>
                                )}
                            />
                        </ColumnsDirective>
                        <Inject services={[Page]} />
                    </GridComponent>
                </div>
            ) : null}
            {!isSearching && tableData.length === 0 && hasSearched && <EmptyData />}
        </div>
    );
}
